import { z } from 'zod';

/**
 * Acquisition tools (ADR-0152, BR-256): where a merchant heard of Arabat, merchant referrals with
 * an operator-granted reward (subscription days, no money), and campaign promo codes whose offer
 * is informational only (nothing is billed today).
 */
export const ACQUISITION_SOURCES = [
  'instagram',
  'tiktok',
  'google',
  'referral',
  'whatsapp',
  'field',
  'other',
] as const;
export type AcquisitionSource = (typeof ACQUISITION_SOURCES)[number];
/** `unknown` = no answer (older or operator-created merchants). */
export const SOURCE_KEYS = [...ACQUISITION_SOURCES, 'unknown'] as const;
export type SourceKey = (typeof SOURCE_KEYS)[number];

export const REFERRAL_CODE_PATTERN = /^[A-Z0-9]{6,12}$/;
export const PROMO_CODE_PATTERN = /^[A-Z0-9_-]{3,32}$/;

const codeInput = (pattern: RegExp) =>
  z
    .string()
    .trim()
    .max(40)
    .nullish()
    .transform((v) => (v ? v.toUpperCase() : null))
    .refine((v) => v === null || pattern.test(v), { error: 'Invalid code.' });

/** Sent with a sign-up (trial funnel). Every field is optional. */
export const acquisitionInputSchema = z
  .object({
    source: z
      .enum(ACQUISITION_SOURCES)
      .nullish()
      .transform((v) => v ?? null),
    sourceOther: z
      .string()
      .trim()
      .max(120)
      .nullish()
      .transform((v) => (v ? v : null)),
    /** The `ref` link parameter: a merchant's referral code. Unknown codes are ignored. */
    referralCode: codeInput(REFERRAL_CODE_PATTERN).catch(null),
    /** The `code` link parameter or typed code: refused when unusable (PROMOTION_INVALID). */
    promoCode: codeInput(PROMO_CODE_PATTERN),
  })
  .transform((v) => ({ ...v, sourceOther: v.source === 'other' ? v.sourceOther : null }));
export type AcquisitionInput = z.input<typeof acquisitionInputSchema>;

// ---------------------------------------------------------------- manager
export const referralLinkSchema = z.object({
  code: z.string(),
  url: z.string(),
  /** Merchants that signed up with this code. */
  referred: z.number().int(),
  /** Rewards granted by Arabat for them. */
  rewarded: z.number().int(),
  /** Days added per granted reward (operator setting). */
  rewardDays: z.number().int(),
});
export type ReferralLink = z.infer<typeof referralLinkSchema>;

// ---------------------------------------------------------------- platform
export const growthSettingsSchema = z.object({
  referralRewardDays: z.number().int(),
  version: z.number().int(),
});
export type GrowthSettings = z.infer<typeof growthSettingsSchema>;
export const saveGrowthSettingsSchema = z.object({
  referralRewardDays: z.number().int().min(1).max(365),
  version: z.number().int().nonnegative(),
});
export type SaveGrowthSettings = z.input<typeof saveGrowthSettingsSchema>;

export const sourceBreakdownSchema = z.object({
  sources: z.array(
    z.object({
      source: z.enum(SOURCE_KEYS),
      merchants: z.number().int(),
      /** Active merchant with an active subscription or a running trial. */
      active: z.number().int(),
      /** Active merchant with an active (subscribed) subscription. */
      subscribed: z.number().int(),
    }),
  ),
});
export type SourceBreakdown = z.infer<typeof sourceBreakdownSchema>;

export const REFERRAL_STATUSES = ['pending', 'rewarded'] as const;
export const referralSchema = z.object({
  referredMerchantId: z.string(),
  referredNameEn: z.string().nullable(),
  referredNameAr: z.string().nullable(),
  referredStatus: z.enum(['active', 'suspended', 'closed']),
  subscriptionStatus: z.string().nullable(),
  trialEndsAt: z.string().nullable(),
  referrerMerchantId: z.string(),
  referrerNameEn: z.string().nullable(),
  referrerNameAr: z.string().nullable(),
  referralCode: z.string().nullable(),
  signedUpAt: z.string(),
  status: z.enum(REFERRAL_STATUSES),
  rewardDays: z.number().int().nullable(),
  rewardedAt: z.string().nullable(),
  rewardedBy: z.string().nullable(),
});
export type Referral = z.infer<typeof referralSchema>;
export const referralListSchema = z.object({
  referrals: z.array(referralSchema),
  total: z.number().int(),
});
export const referralQuerySchema = z.object({ referrer: z.uuid().optional() });

export const rewardResultSchema = z.object({
  referral: referralSchema,
  extended: z.enum(['trial_ends_at', 'ends_at']),
  newEnd: z.string(),
});
export type RewardResult = z.infer<typeof rewardResultSchema>;

const optionalDateTime = z
  .string()
  .datetime({ offset: true })
  .nullish()
  .or(z.literal(''))
  .transform((v) => (v ? v : null));

export const savePromoCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .transform((v) => v.toUpperCase())
    .pipe(z.string().regex(PROMO_CODE_PATTERN)),
  label: z.string().trim().min(1).max(120),
  offer: z.string().trim().min(1).max(500),
  expiresAt: optionalDateTime,
  maxUses: z
    .number()
    .int()
    .min(1)
    .max(1_000_000)
    .nullish()
    .transform((v) => v ?? null),
  active: z.boolean(),
  /** 0 when creating. */
  version: z.number().int().nonnegative(),
});
export type SavePromoCode = z.input<typeof savePromoCodeSchema>;

export const promoCodeSchema = z.object({
  id: z.string(),
  code: z.string(),
  label: z.string(),
  offer: z.string(),
  expiresAt: z.string().nullable(),
  maxUses: z.number().int().nullable(),
  active: z.boolean(),
  /** Sign-ups that used the code. */
  uses: z.number().int(),
  /** Of them, merchants in use now. */
  activeUses: z.number().int(),
  /** Usable for a new sign-up now (active, not expired, uses left). */
  usable: z.boolean(),
  createdAt: z.string(),
  version: z.number().int(),
});
export type PromoCode = z.infer<typeof promoCodeSchema>;
export const promoCodeListSchema = z.object({ codes: z.array(promoCodeSchema) });

export const merchantAcquisitionSchema = z.object({
  source: z.enum(SOURCE_KEYS),
  sourceOther: z.string().nullable(),
  referrerMerchantId: z.string().nullable(),
  referrerNameEn: z.string().nullable(),
  referrerNameAr: z.string().nullable(),
  referralCode: z.string().nullable(),
  promoCode: z.string().nullable(),
  promoLabel: z.string().nullable(),
  ownReferralCode: z.string().nullable(),
  referredCount: z.number().int(),
  rewardedCount: z.number().int(),
  recordedAt: z.string().nullable(),
});
export type MerchantAcquisition = z.infer<typeof merchantAcquisitionSchema>;
